import { useMemo, useEffect, useCallback, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useChurchConfig, saveOptionList, hasServerConfig } from './useChurchConfig';
import { parseOptionLists, optionListDefaults } from '../utils/optionLists';

// A parish-wide suggestion list (event types, expense categories, personnel
// names, ...). The lists live on the server in ChurchConfig.optionLists, so a
// name added in one form shows up for every user — and Settings › Dropdown
// Lists edits them all in one place.
//
// Use this when TWO OR MORE SearchableSelects share the same list — e.g.
// Collections' "Counted By" and "Verified By" both draw from one personnel
// roster. For a list used by a single dropdown, just pass `storageKey` to
// SearchableSelect, which calls this hook for you.
//
//   const personnel = useOptionList('church_personnel_list');
//   <SearchableSelect options={personnel.options} onOptionsChange={personnel.setOptions} ... />
//
// `defaults` is only needed for a key the registry in utils/optionLists does
// not know; registered lists take their starting values from there.

export interface UseOptionListResult {
  options: string[];
  setOptions: Dispatch<SetStateAction<string[]>>;
  /** Add values if missing — used on submit for typed-but-not-added entries. */
  ensure: (...values: Array<string | undefined | null>) => void;
}

// Before the lists moved to the server each browser kept its own copy under
// the same key. That copy is read once, as the starting point for a list the
// server has not seen yet, so nothing a parish typed in is lost.
const readLegacyLocal = (key: string): string[] | null => {
  if (!key) return null;
  try {
    const stored = JSON.parse(localStorage.getItem(key) || 'null');
    if (!Array.isArray(stored)) return null;
    const values = stored.filter((v): v is string => typeof v === 'string');
    return values.length ? values : null;
  } catch {
    return null;
  }
};

export const useOptionList = (
  storageKey: string,
  defaults?: readonly string[]
): UseOptionListResult => {
  const config = useChurchConfig();

  const serverList = useMemo(
    () => parseOptionLists(config.optionLists)[storageKey],
    [config.optionLists, storageKey]
  );

  const options = useMemo<string[]>(() => {
    if (serverList) return serverList;
    return readLegacyLocal(storageKey) || [...(defaults ?? optionListDefaults(storageKey))];
  }, [serverList, storageKey, defaults]);

  // Seed the server from the browser's old copy — once the real configuration
  // has loaded, or a slow network would let the local copy overwrite the
  // server's list. Runs again only if the list is still absent.
  useEffect(() => {
    if (!storageKey || serverList || !hasServerConfig()) return;
    const legacy = readLegacyLocal(storageKey);
    if (legacy) saveOptionList(storageKey, legacy);
  }, [storageKey, serverList, config]);

  // Updater-style setOptions(prev => ...) needs the latest list without
  // re-creating the callback on every change.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const setOptions = useCallback<Dispatch<SetStateAction<string[]>>>((next) => {
    if (!storageKey) return;
    const value = typeof next === 'function' ? next(optionsRef.current) : next;
    saveOptionList(storageKey, value);
  }, [storageKey]);

  // Ensure a value is present — used on submit so a typed-but-not-added
  // entry still joins the roster.
  const ensure = useCallback((...values: Array<string | undefined | null>) => {
    const wanted = values.map((v) => String(v || '').trim()).filter(Boolean);
    if (!wanted.length) return;
    const prev = optionsRef.current;
    // Case-insensitively, so "Annual Leave" and "annual leave" are the same
    // entry rather than two — the duplicate-on-uppercase bug across the option
    // lists (leave types, designations, event/service types, roles, classes).
    // The seen-set grows as we add, so two case-variants passed in one call
    // (e.g. "Media" and "MEDIA") don't both get through either.
    const seen = new Set(prev.map((v) => v.trim().toLowerCase()));
    const missing: string[] = [];
    wanted.forEach((v) => {
      const k = v.toLowerCase();
      if (!seen.has(k)) { seen.add(k); missing.push(v); }
    });
    if (missing.length) setOptions([...prev, ...missing]);
  }, [setOptions]);

  return { options, setOptions, ensure };
};

export default useOptionList;
